import { BorderRadiusTokenData, ColorTokenData, FontFamilyTokenData, FontSizeTokenData, FontWeightTokenData, LineHeightTokenData, ShadowTokenData, SpaceTokenData, } from "@supernova-studio/model";
import { zodQueryBoolean } from "../../../utils/query";
import { DTOObjectMetadata } from "../aux";
import { DTOFileReference } from "../files/files";
import { DTOForgeMcpUrlSlug, DTOForgeProjectContextScope, } from "./project-context-scope";
import { z } from "zod";
export const DTOForgeComponentSetTypeV2 = z.enum(["Shadcn"]);
export const DTOForgeComponentSet = z.object({
    type: DTOForgeComponentSetTypeV2,
});
export const DTOForgeIconSetTypeV2 = z.enum(["Phosphor", "Lucide", "Tabler"]);
export const DTOForgeThemeKnownPreset = z.enum([
    "Default",
    "Airbnb",
    "Spotify",
    "Windows98",
]);
export const DTOForgeIconSet = z.object({
    type: DTOForgeIconSetTypeV2,
    variant: z.string().optional(),
});
export const DTOForgeTokenThemeSet = z.object({
    id: z.string(),
    name: z.string(),
    tokenThemeIds: z.array(z.string()),
});
export const DTOForgeProjectTheme = z.object({
    // Colors
    background: ColorTokenData,
    foreground: ColorTokenData,
    card: ColorTokenData,
    cardForeground: ColorTokenData,
    popover: ColorTokenData,
    popoverForeground: ColorTokenData,
    primary: ColorTokenData,
    primaryForeground: ColorTokenData,
    secondary: ColorTokenData,
    secondaryForeground: ColorTokenData,
    muted: ColorTokenData,
    mutedForeground: ColorTokenData,
    accent: ColorTokenData,
    accentForeground: ColorTokenData,
    destructive: ColorTokenData,
    border: ColorTokenData,
    input: ColorTokenData,
    ring: ColorTokenData,
    chart1: ColorTokenData,
    chart2: ColorTokenData,
    chart3: ColorTokenData,
    chart4: ColorTokenData,
    chart5: ColorTokenData,
    sidebar: ColorTokenData,
    sidebarForeground: ColorTokenData,
    sidebarPrimary: ColorTokenData,
    sidebarPrimaryForeground: ColorTokenData,
    sidebarAccent: ColorTokenData,
    sidebarAccentForeground: ColorTokenData,
    sidebarBorder: ColorTokenData,
    sidebarRing: ColorTokenData,
    // Font family
    fontFamily: FontFamilyTokenData,
    // Font sizes
    textXs: FontSizeTokenData,
    textSm: FontSizeTokenData,
    textBase: FontSizeTokenData,
    textLg: FontSizeTokenData,
    textXl: FontSizeTokenData,
    text2xl: FontSizeTokenData,
    text3xl: FontSizeTokenData,
    text4xl: FontSizeTokenData,
    text5xl: FontSizeTokenData,
    text6xl: FontSizeTokenData,
    text7xl: FontSizeTokenData,
    text8xl: FontSizeTokenData,
    text9xl: FontSizeTokenData,
    // Line heights
    textXsLineHeight: LineHeightTokenData,
    textSmLineHeight: LineHeightTokenData,
    textBaseLineHeight: LineHeightTokenData,
    textLgLineHeight: LineHeightTokenData,
    textXlLineHeight: LineHeightTokenData,
    text2xlLineHeight: LineHeightTokenData,
    text3xlLineHeight: LineHeightTokenData,
    text4xlLineHeight: LineHeightTokenData,
    text5xlLineHeight: LineHeightTokenData,
    text6xlLineHeight: LineHeightTokenData,
    text7xlLineHeight: LineHeightTokenData,
    text8xlLineHeight: LineHeightTokenData,
    text9xlLineHeight: LineHeightTokenData,
    // Font weights
    fontWeightThin: FontWeightTokenData,
    fontWeightExtralight: FontWeightTokenData,
    fontWeightLight: FontWeightTokenData,
    fontWeightNormal: FontWeightTokenData,
    fontWeightMedium: FontWeightTokenData,
    fontWeightSemibold: FontWeightTokenData,
    fontWeightBold: FontWeightTokenData,
    fontWeightExtrabold: FontWeightTokenData,
    fontWeightBlack: FontWeightTokenData,
    // Other dimensions
    radius: BorderRadiusTokenData,
    spacing: SpaceTokenData,
    // Shadows
    shadow2xs: ShadowTokenData,
    shadowXs: ShadowTokenData,
    shadowSm: ShadowTokenData,
    shadowMd: ShadowTokenData,
    shadowLg: ShadowTokenData,
    shadowXl: ShadowTokenData,
    shadow2xl: ShadowTokenData,
});
export const DTOForgeProjectContextV2 = z.object({
    id: z.string(),
    name: z.string(),
    workspaceId: z.string(),
    designSystemId: z.string().optional(),
    brandId: z.string().optional(),
    defaultTokenThemeSetId: z.string().optional(),
    description: z.string().optional(),
    productContext: z.string().optional(),
    additionalContext: z.string().optional(),
    isArchived: z.boolean(),
    isAllowedInPortal: z.boolean(),
    isFeedbackCollectionEnabled: z.boolean(),
    isFeedbackCollectionAnonymous: z.boolean(),
    isLibraryDismissed: z.boolean().optional(),
    themePreset: z.string().optional(),
    tokenThemeSets: z.array(DTOForgeTokenThemeSet).optional(),
    includedContextIds: z.array(z.string()).default([]),
    includedByContextIds: z.array(z.string()).default([]),
    scope: DTOForgeProjectContextScope.nullish(),
    mcpUrlSlug: DTOForgeMcpUrlSlug.nullish(),
    metadata: DTOObjectMetadata.optional(),
    componentSet: DTOForgeComponentSet,
    iconSet: DTOForgeIconSet,
    theme: DTOForgeProjectTheme,
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    thumbnail: DTOFileReference.optional(),
    sandboxTemplate: z
        .object({
        id: z.string(),
        version: z.string(),
    })
        .optional(),
});
export const DTOForgeProjectContextCreateV2 = DTOForgeProjectContextV2.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    isArchived: true,
    thumbnail: true,
    includedByContextIds: true,
})
    .extend({
    // Optional on create: FE may pass an explicit value, otherwise the server
    // derives it (true when a sandbox template or component library is provided, else false).
    isAllowedInPortal: z.boolean().optional(),
    isFeedbackCollectionEnabled: z.boolean().optional(),
    isFeedbackCollectionAnonymous: z.boolean().optional(),
    thumbnailFileId: z.string().optional(),
    theme: DTOForgeProjectTheme.optional(),
    iconSet: DTOForgeIconSet.optional(),
    componentSet: DTOForgeComponentSet.optional(),
    includedContextIds: z.array(z.string()).optional(),
})
    .superRefine((input, ctx) => {
    // If FE sets isAllowedInPortal to false => we don't care about templates/component lib
    if (input.isAllowedInPortal === false)
        return;
    const hasDefaultTemplateFields = input.theme && input.iconSet && input.componentSet;
    const hasCustomTemplateFields = input.sandboxTemplate;
    if (hasDefaultTemplateFields || hasCustomTemplateFields)
        return;
    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Either the setup fields for default template (theme, iconSet, componentSet) or a sandbox template field (sandboxTemplate) must be provided.",
        path: ["theme", "iconSet", "componentSet", "sandboxTemplate"],
    });
});
export const DTOForgeProjectContextUpdateV2 = DTOForgeProjectContextV2.omit({
    id: true,
    workspaceId: true,
    createdAt: true,
    updatedAt: true,
    thumbnail: true,
    includedByContextIds: true,
})
    .partial()
    .extend({
    sandboxTemplate: DTOForgeProjectContextV2.shape.sandboxTemplate.nullable(),
    thumbnailFileId: z.string().nullish(),
});
export const DTOForgeProjectContextResponseV2 = z.object({
    context: DTOForgeProjectContextV2,
});
export const DTOForgeProjectContextListQueryV2 = z.object({
    workspaceId: z.string(),
    isArchived: zodQueryBoolean(),
});
export const DTOForgeProjectContextListResponseV2 = z.object({
    contexts: z.array(DTOForgeProjectContextV2),
});
